import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, GripVertical, Plus, Search, X } from 'lucide-react'
import type { FieldDefinition } from '@/api/types'
import type { ContactViewConfig } from '@/api/contactViews'
import {
  COLUMNS,
  CUSTOM_COLUMN_PREFIX,
  DEFAULT_TABLE_SETTINGS,
  customContactColumns,
  type ColumnId,
  type TableSettings,
} from '@/features/contacts/contactsTableSettings'
import {
  useContactViews,
  useCreateContactView,
  useDeleteContactView,
} from '@/features/contacts/useContactViews'
import { cn } from '@/lib/cn'

/**
 * The "Columns & Views" right drawer for the Contacts table (mockup #165): apply/save a view, then
 * toggle, search and reorder visible columns and add available/custom fields. Contacts-specific (it
 * embeds saved views), so the shared deals table keeps its lighter popover.
 */
export default function ContactsColumnsDrawer({
  settings,
  onSettingsChange,
  fieldDefs,
  currentView,
  onApplyView,
}: {
  settings: TableSettings
  onSettingsChange: (next: TableSettings) => void
  fieldDefs: FieldDefinition[]
  /** Current filters/sort/columns, saved when the user creates a view. */
  currentView: ContactViewConfig
  onApplyView: (config: ContactViewConfig) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [viewName, setViewName] = useState('')
  const [shared, setShared] = useState(false)

  const { data: views } = useContactViews()
  const createView = useCreateContactView()
  const removeView = useDeleteContactView()

  const allColumns = useMemo(() => [...COLUMNS, ...customContactColumns(fieldDefs)], [fieldDefs])
  const byId = useMemo(() => new Map(allColumns.map((c) => [c.id, c])), [allColumns])
  const lockedIds = useMemo(() => new Set(allColumns.filter((c) => c.locked).map((c) => c.id)), [allColumns])

  const visible = settings.columns.map((id) => byId.get(id)).filter((c): c is (typeof allColumns)[number] => !!c)
  const hidden = allColumns.filter((c) => !settings.columns.includes(c.id))
  const ql = query.trim().toLowerCase()
  const shownVisible = visible.map((col, i) => ({ col, i })).filter(({ col }) => !ql || col.label.toLowerCase().includes(ql))
  const hiddenBuiltin = hidden.filter((c) => !c.id.startsWith(CUSTOM_COLUMN_PREFIX) && (!ql || c.label.toLowerCase().includes(ql)))
  const hiddenCustom = hidden.filter((c) => c.id.startsWith(CUSTOM_COLUMN_PREFIX) && (!ql || c.label.toLowerCase().includes(ql)))

  function toggle(id: ColumnId) {
    if (settings.columns.includes(id)) {
      if (lockedIds.has(id)) return
      onSettingsChange({ ...settings, columns: settings.columns.filter((c) => c !== id) })
    } else {
      onSettingsChange({ ...settings, columns: [...settings.columns, id] })
    }
  }

  function move(i: number, dir: -1 | 1) {
    const j = i + dir
    if (j < 0 || j >= visible.length) return
    if (lockedIds.has(visible[i].id) || lockedIds.has(visible[j].id)) return
    const cols = [...settings.columns]
    const ai = cols.indexOf(visible[i].id)
    const aj = cols.indexOf(visible[j].id)
    ;[cols[ai], cols[aj]] = [cols[aj], cols[ai]]
    onSettingsChange({ ...settings, columns: cols })
  }

  function saveView() {
    if (!viewName.trim()) return
    createView.mutate(
      { name: viewName.trim(), shared, config: currentView },
      { onSuccess: () => { setViewName(''); setShared(false); setSaving(false) } },
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 bg-white hover:bg-gray-50"
      >
        <GripVertical size={14} /> Columns &amp; Views
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative w-80 max-w-[95vw] h-full bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-brand-darkest">Columns &amp; Views</h2>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600" title="Close">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              <section>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">View</p>
                <div className="space-y-1">
                  {(views ?? []).length === 0 && <p className="text-xs text-gray-400">No saved views yet.</p>}
                  {(views ?? []).map((v) => (
                    <div key={v.id} className="flex items-center gap-1.5 rounded-lg hover:bg-gray-50 px-2 py-1.5">
                      <button type="button" onClick={() => { onApplyView(v.config); setOpen(false) }} className="flex-1 min-w-0 text-left text-sm text-brand-dark truncate">
                        {v.name}
                      </button>
                      {v.shared && <span className="text-[10px] text-gray-400">shared</span>}
                      {v.mine && (
                        <button onClick={() => removeView.mutate(v.id)} disabled={removeView.isPending} className="text-gray-300 hover:text-danger" title="Delete view">
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search columns…"
                  className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                />
              </div>

              <section>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                  Visible columns <span className="text-gray-300">{visible.length}</span>
                </p>
                <div className="space-y-0.5">
                  {shownVisible.map(({ col, i }) => (
                    <div key={col.id} className="flex items-center gap-1.5 text-sm text-brand-dark px-1 py-1 rounded hover:bg-gray-50">
                      <GripVertical size={13} className="text-gray-300 shrink-0" />
                      <span className={cn('flex-1 truncate', col.locked && 'text-gray-400')}>{col.label}</span>
                      {!ql && (
                        <>
                          <button type="button" disabled={i === 0 || lockedIds.has(col.id)} onClick={() => move(i, -1)} className="text-gray-300 hover:text-brand-primary disabled:opacity-30" aria-label="Move up">
                            <ArrowUp size={12} />
                          </button>
                          <button type="button" disabled={i === visible.length - 1 || lockedIds.has(col.id)} onClick={() => move(i, 1)} className="text-gray-300 hover:text-brand-primary disabled:opacity-30" aria-label="Move down">
                            <ArrowDown size={12} />
                          </button>
                        </>
                      )}
                      <Switch on={true} disabled={col.locked} onToggle={() => toggle(col.id)} />
                    </div>
                  ))}
                </div>
              </section>

              {hiddenBuiltin.length > 0 && (
                <section>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Available fields</p>
                  <div className="space-y-0.5">
                    {hiddenBuiltin.map((col) => (
                      <button key={col.id} type="button" onClick={() => toggle(col.id)} className="flex items-center justify-between w-full text-sm text-brand-dark px-1 py-1 rounded hover:bg-gray-50">
                        <span className="truncate">{col.label}</span>
                        <Plus size={14} className="text-gray-400" />
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {hiddenCustom.length > 0 && (
                <section>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Custom fields</p>
                  <div className="space-y-0.5">
                    {hiddenCustom.map((col) => (
                      <button key={col.id} type="button" onClick={() => toggle(col.id)} className="flex items-center justify-between w-full text-sm text-brand-dark px-1 py-1 rounded hover:bg-gray-50">
                        <span className="truncate">{col.label}</span>
                        <Plus size={14} className="text-gray-400" />
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="p-4 border-t border-gray-100">
              {saving ? (
                <div className="space-y-2">
                  <input autoFocus value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="View name" onKeyDown={(e) => e.key === 'Enter' && saveView()} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="h-4 w-4" /> Share with the team
                  </label>
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setSaving(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-brand-dark">Cancel</button>
                    <button onClick={saveView} disabled={!viewName.trim() || createView.isPending} className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60">
                      {createView.isPending ? 'Saving…' : 'Save view'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <button onClick={() => onSettingsChange({ ...settings, columns: DEFAULT_TABLE_SETTINGS.columns })} className="text-xs text-gray-500 hover:text-brand-dark">
                    Reset to default
                  </button>
                  <button onClick={() => setSaving(true)} className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark">
                    Save view
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Switch({ on, disabled, onToggle }: { on: boolean; disabled?: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn('relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 disabled:opacity-40', on ? 'bg-brand-primary' : 'bg-gray-300')}
      aria-pressed={on}
    >
      <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform', on ? 'translate-x-4' : 'translate-x-0.5')} />
    </button>
  )
}
